import { useState } from "react";
import { Link as RouterLink } from "react-router";
import {
  Typography,
  Menu,
  MenuItem,
  Button,
  Box,
  Card,
  CardContent,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Person from "@mui/icons-material/Person";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import TimelineIcon from "@mui/icons-material/Timeline";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";
import MemberList from "@nualang/nualang-ui-components/dist/Lists/Members";
import MembersDialog from "@nualang/nualang-ui-components/dist/Dialogs/Members";
import ResponsiveDialog from "@nualang/nualang-ui-components/dist/Dialogs/ResponsiveDialog";
import CreateSectionDialog from "@nualang/nualang-ui-components/dist/Dialogs/CreateSection";
import InviteDialog from "@nualang/nualang-ui-components/dist/Dialogs/InviteDialog";
import ShareDrawer from "@nualang/nualang-ui-components/dist/Misc/ShareButton/ShareDrawer";
import CourseNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Course";
import SectionsNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Sections";
import ProgressiveCardMedia from "@nualang/nualang-ui-components/dist/Misc/ProgressiveCardMedia";
import TopicDifficultyBadge from "@nualang/nualang-ui-components/dist/Misc/TopicDifficultyBadge";
import UpdateCourse from "@nualang/nualang-ui-components/dist/Forms/UpdateCourse";
import CourseOutline from "@nualang/nualang-ui-components/dist/Lists/CourseOutline";
import PlaceholderImages from "@nualang/nualang-ui-components/dist/utils/placeholder-images";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import CreatorNotSubscribed from "@nualang/nualang-ui-components/dist/Dialogs/CreatorNotSubscribed/CreatorNotSubscribed";
import Overlay from "@nualang/nualang-ui-components/dist/Misc/Overlay";
import DefaultButton from "@nualang/nualang-ui-components/dist/Misc/DefaultColourButton";
import { CardAvatar, CardMenuIcon } from "@nualang/nualang-ui-components/dist/Cards/CardElements";
import PdfUploader from "@nualang/nualang-ui-components/dist/Forms/PdfUploader";
import PDFViewer from "@nualang/nualang-ui-components/dist/Dialogs/PDFViewer/PDFViewer";
import UploadVideo from "@nualang/nualang-ui-components/dist/Dialogs/UploadVideo";
import VideoViewer from "@nualang/nualang-ui-components/dist/Dialogs/VideoViewer/VideoViewer";
import { glowPulseLight, glowPulseDark } from "@nualang/nualang-ui-components/dist/utils/Styles";
import ShareIcon from "@mui/icons-material/Share";
import Close from "@mui/icons-material/Close";
import ExitToApp from "@mui/icons-material/ExitToApp";
import { Add } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import OndemandVideo from "@mui/icons-material/OndemandVideo";
import SchoolIcon from "@mui/icons-material/School";
import MailOutlineTwoToneIcon from "@mui/icons-material/MailOutlineTwoTone";

import logger from "@nualang/nualang-ui-components/dist/logger";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  cardTop: {
    position: "relative",
  },
  cardTopAvatar: {
    [theme.breakpoints.down("md")]: {
      width: "60px",
      height: "60px",
    },
    width: "75px",
    height: "75px",
    border: `3px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
    color:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : "white",
    cursor: "pointer",
    transition: `transform 0.2s ease 0s`,
    "&:hover": {
      transform: `scale(1.08)`,
    },
  },
  header: {
    display: "flex",
    alignItems: "start",
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
  headerText: {
    flexGrow: 1,
  },
  headerTextTitle: {
    fontWeight: 600,
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2),
  },
  inviteButton: {
    marginRight: theme.spacing(),
  },
  coursesTab: {
    position: "relative",
  },
}));

function OverflowMenu({
  t,
  courseId,
  anchorEl,
  handleClose,
  handleOpenInviteDialog,
  isCreator,
  isMember,
  joinCourse,
  leaveCourse,
  deleteCourse,
  subscription,
  handleClickOpenUpload,
  pdfUrl,
  deletePDF,
  deleteVideo,
  videoUrl,
  courseName,
  trackRecommendedEvent,
  mainSiteUrl,
  loading,
  verificationStatus,
  handleUploadVideo,
  isTeacher,
  video,
  setVideo,
  handleCloseMenu
}) {
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [isUploadVideoOpen, setIsUploadVideoOpen] = useState(false);
  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  const handleOpenUpgradeSubscriptionModal = () => {
    setIsUpgradeSubscriptionOpen(true);
  };

  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };
  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const handleClick = () => {
    if (!subscription.isSubscriptionLoaded || subscription.isPaidUser) {
      setIsShareDrawerOpen(true);
    } else {
      handleOpenUpgradeSubscriptionModal();
    }
  };

  return (
    <>
      <Menu
        key={`card-menu-${courseId}`}
        id={`card-menu-${courseId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {isTeacher && (
          <MenuItem onClick={handleClick} data-cy='share-course'>
            <CardMenuIcon>
              <ShareIcon fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("share")}</Typography>
          </MenuItem>
        )}
        {!isCreator && !isMember && (
          <MenuItem onClick={joinCourse}>
            <CardMenuIcon>
              <Add fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("join_course")}</Typography>
          </MenuItem>
        )}
        {!isCreator && isMember && (
          <MenuItem onClick={leaveCourse}>
            <CardMenuIcon>
              <ExitToApp fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("leave_course")}</Typography>
          </MenuItem>
        )}
        {isCreator && (
          <MenuItem
            onClick={
              !subscription.isSubscriptionLoaded || subscription.isPaidUser
                ? () => {
                    handleOpenInviteDialog();
                    handleClose();
                  }
                : handleOpenUpgradeSubscriptionModal
            }
            data-cy='invite'
          >
            <CardMenuIcon>
              <MailOutlineTwoToneIcon fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("invite")}</Typography>
          </MenuItem>
        )}
        {isCreator && verificationStatus === "approved" && (
          <MenuItem onClick={handleClickOpenUpload} data-cy='upload-pdf'>
            <CardMenuIcon>
              <PictureAsPdfIcon fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("upload_pdf")}</Typography>
          </MenuItem>
        )}
        {pdfUrl && isCreator && verificationStatus === "approved" && (
          <MenuItem onClick={deletePDF} data-cy='delete-pdf'>
            <CardMenuIcon>
              <Delete fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("delete_pdf")}</Typography>
          </MenuItem>
        )}
        {isCreator && verificationStatus === "approved" && (
          <MenuItem
            onClick={() => setIsUploadVideoOpen(true)}
            data-cy='upload-video'
          >
            <CardMenuIcon>
              <OndemandVideo fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("upload_video")}</Typography>
          </MenuItem>
        )}
        {videoUrl && isCreator && verificationStatus === "approved" && (
          <MenuItem onClick={deleteVideo} data-cy='delete-video'>
            <CardMenuIcon>
              <Delete fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("delete_video")}</Typography>
          </MenuItem>
        )}
        {isCreator && (
          <MenuItem onClick={deleteCourse} data-cy='delete-course'>
            <CardMenuIcon>
              <Close fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("delete_course")}</Typography>
          </MenuItem>
        )}
      </Menu>
      <ShareDrawer
        t={t}
        title={courseName}
        url={window.location.href}
        open={isShareDrawerOpen}
        handleClose={() => {
          setIsShareDrawerOpen(false)
          handleCloseMenu();
        }
          }
        subscription={subscription}
        trackRecommendedEvent={trackRecommendedEvent}
        itemId={courseId}
        contentType='course'
      />
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
      <UploadVideo
        t={t}
        open={isUploadVideoOpen}
        onSubmit={handleUploadVideo}
        loading={loading}
        video={video}
        setVideo={setVideo}
        handleClose={() => setIsUploadVideoOpen(false)}
      />
    </>
  );
}

function Course({
  t,
  isLoading,
  isSectionsLoading,
  course,
  isMember,
  sections = [],
  members = [],
  handleStartCourse,
  handleLeaveCourse,
  handleDeleteCourse,
  handleCreateCourseSection,
  handleUpdateCourseSection,
  handleDeleteCourseSection,
  handleCreateCourseSectionTopic,
  handleDuplicateTopic,
  handleUpdateSectionTopic,
  handleDeleteCourseSectionTopic,
  handleViewCourseTopic,
  membersProps,
  languages,
  forLanguages,
  getMemberDetails,
  handleUpdateCourse,
  handleUpdateSections,
  handleUpdateTopics,
  handleMoveTopic,
  memberCompletions = [],
  courseCompletions = [],
  isPublic,
  handleSwitchChange,
  searchTopics,
  memberPlaceholderImageUrl,
  fileSizeLimit,
  handleViewUserProfile,
  subscription,
  creatorSubscription,
  handleOpenAddToClassroom,
  handleOpenSelectClassroomDialog,
  teachClassrooms,
  isStudent,
  mainSiteUrl,
  attributes,
  trackRecommendedEvent,
  handleSignIn,
  authenticated,
  addCourseToClassroom,
  pdfUrl,
  pdfTitle,
  handleDeletePDF,
  videoUrl,
  handleDeleteCourseVideo,
  handleDeleteSectionPDF,
  courseSettings,
  classroomId,
  loading,
  featureFlags,
  isChallengeModeStudent,
  isReadWriteModeStudent,
  handleQuickCreateCourseSectionTopic,
  handleAddTopicsToSection,
  libraryTopics,
  communityTopics,
  onLoadMoreCommunity,
  hasMoreCommunity,
  isLoadingMoreCommunity,
  openSnackbar,
  ...otherProps
}) {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirm] = useConfirm(t);
  const theme = useTheme();

  const [file, setFile] = useState(null);
  const [isUploadPdfOpen, setIsUploadPdfOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [animatePlayVideo, setAnimatePlayVideo] = useState(true);
  const [video, setVideo] = useState();

  const handleUploadSubmit = async () => {
    setIsSubmitting(true);
    try {
      await handleUpdateCourse({ pdf: file }, isSubmitting);
    } catch (error) {
      logger.error(error);
    }
    setIsUploadPdfOpen(false);
    setIsSubmitting(false);
    setFile(null);
    handleCloseMenu();
  };

  const handleUploadVideo = async (video) => {
    setIsSubmitting(true);
    try {
      await handleUpdateCourse({ video }, isSubmitting);
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
    handleCloseMenu();
  };
  const handleCLickOpenUpload = () => {
    setIsUploadPdfOpen(true);
  };

  const handleClickOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const handleClickOpenMembers = () => {
    setIsMembersOpen(true);
  };
  const handleCloseMembers = () => {
    setIsMembersOpen(false);
  };

  const [isCreateSectionOpen, setIsCreateSectionOpen] = useState(false);
  const handleClickOpenCreateSection = () => {
    setIsCreateSectionOpen(true);
  };
  const handleCloseCreateSection = () => {
    setIsCreateSectionOpen(false);
  };

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const handleOpenInviteDialog = () => {
    setIsInviteDialogOpen(true);
  };
  const handleCloseInviteDialog = () => {
    setIsInviteDialogOpen(false);
  };

  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  const handleOpenUpgradeSubscriptionModal = () => {
    setIsUpgradeSubscriptionOpen(true);
  };
  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };

  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const [isCreatorNotSubscribedOpen, setIsCreatorNotSubscribedOpen] =
    useState(false);
  const handleOpenCreatorNotSubscribedModal = () => {
    setIsCreatorNotSubscribedOpen(true);
  };
  const handleCloseCreatorNotSubscribedModal = () => {
    setIsCreatorNotSubscribedOpen(false);
  };

  const [isViewPdfOpen, setIsViewPdfOpen] = useState(false);
  const [isViewVideoOpen, setIsViewVideoOpen] = useState(false);

  const handleOpenViewPDF = () => {
    setIsViewPdfOpen(true);
  };

  const handleOpenViewVideo = () => {
    setIsViewVideoOpen(true);
    setAnimatePlayVideo(false);
  };

  const handleCloseViewPdf = () => {
    setIsViewPdfOpen(false);
  };

  const initialSectionState = { sectionName: "", picture: "" };
  const [section, setSection] = useState(initialSectionState);

  const handleSectionChange = (event) => {
    const value = event.target.value;
    const name = event.target.name;
    setSection((prevSection) => ({
      ...prevSection,
      [name]: value,
    }));
  };

  const loadingTabs = [
    {
      label: t("course"),
      id: "Course",
    },
    {
      label: t("members"),
      id: "Members",
    },
  ];

  if (!course) {
    let initialTabValue;
    if (window.location.hash) {
      const selectedTab = window.location.hash;
      initialTabValue = loadingTabs.findIndex(
        (tab) => tab.id === selectedTab.replace("#", ""),
      );
      if (initialTabValue < 0) {
        initialTabValue = 0;
      }
    } else {
      initialTabValue = 0;
    }

    return (
      <>
        <div className={classes.cardTop}>
          <Skeleton
            variant='rectangular'
            sx={(theme) => ({
              [theme.breakpoints.up("md")]: {
                paddingTop: "15%",
              },
              height: 0,
              paddingTop: "25%",
              borderRadius: theme.spacing(2),
              marginBottom: theme.spacing(4),
            })}
          />
        </div>
        <div className={classes.header}>
          <div className={classes.headerText}>
            <Typography variant='h5' component='h1' gutterBottom>
              <Skeleton variant='rectangular' height={32} width={150} />
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              <Skeleton variant='rectangular' height={20} width={250} />
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 2,
                mb: 1
              }}>
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<PersonAddIcon />}
                  size='small'
                  disabled
                >
                  {t("join")}
                </Button>
              </Box>
              {authenticated && (
                <DefaultButton
                  startIcon={<MoreVertIcon />}
                  size='small'
                  disabled
                >
                  {t("more_options")}
                </DefaultButton>
              )}
            </Box>
          </div>
        </div>
        <Box sx={{
          py: 1
        }}>
          <Card>
            <CardContent>
              <CourseOutline isLoading />
            </CardContent>
          </Card>
        </Box>
      </>
    );
  }

  const {
    courseId,
    courseName,
    description,
    picture,
    pictureXs,
    banner,
    isCreator,
    isShown,
    userImage,
    verificationStatus,
    createdBy,
    createdByName,
    gameAward,
    bannerXs,
  } = course;

  // Same aggregation as the course card: every distinct topic difficulty
  // across the course's sections, falling back to the course's own level.
  const topicDifficulties = [
    ...new Set(
      sections
        .flatMap((section) => section.topics || [])
        .map((topic) => topic.difficulty)
        .filter(Boolean),
    ),
  ];
  const bannerDifficulty =
    topicDifficulties.length > 0 ? topicDifficulties : course?.difficulty;

  const handleJoin = () => {
    if (creatorSubscription.isPaidUser || course.visibility === "public") {
      handleStartCourse();
    } else {
      handleOpenCreatorNotSubscribedModal();
    }
  };

  const handleAddSection = async () => {
    await handleCreateCourseSection(courseId, section);
    setSection(initialSectionState);
    handleCloseCreateSection();
  };

  const deleteCourseSectionTopic = async (courseId, sectionId, topicId) => {
    const courseSection =
      sections && sections.find((section) => section.sectionId === sectionId);
    const courseSectionTopic =
      courseSection &&
      courseSection.topics.find((topic) => topic.topicId === topicId);
    const confirmed = await confirm(
      t("delete_topic"),
      t("delete_topic_confirmation"),
      "",
      true,
      t("topic_deletion_note", {
        roleplayCount:
          courseSectionTopic && courseSectionTopic.roleplays
            ? courseSectionTopic.roleplays.length
            : 0,
        botCount:
          courseSectionTopic && courseSectionTopic.bots
            ? courseSectionTopic.bots.length
            : 0,
        phraseCount:
          courseSectionTopic && courseSectionTopic.phrases
            ? courseSectionTopic.phrases.length
            : 0,
      }),
      t("confirmDeletionText"),
    );

    if (confirmed) {
      handleDeleteCourseSectionTopic(courseId, sectionId, topicId);
    }
  };

  const deleteCourseSection = async (courseId, sectionId) => {
    const courseSection =
      sections && sections.find((section) => section.sectionId === sectionId);
    const confirmed = await confirm(
      t("delete_section"),
      t("delete_section_confirmation"),
      "",
      true,
      t("section_deletion_note", {
        topicCount:
          courseSection && courseSection.topics
            ? courseSection.topics.length
            : 0,
      }),
      t("confirmDeletionText"),
    );

    if (confirmed) {
      handleDeleteCourseSection(courseId, sectionId, courseSection.topics);
    }
  };

  const leaveCourse = async (id) => {
    const confirmed = await confirm(
      t("leave_course"),
      t("leave_course_confirmation"),
    );

    if (confirmed) {
      handleLeaveCourse(id);
    }
  };

  const deleteCourse = async (id) => {
    const confirmed = await confirm(
      t("delete_course"),
      t("delete_course_confirmation"),
      "",
      true,
      t("course_deletion_note", {
        sectionCount: sections ? sections.length : 0,
      }),
      t("confirmDeletionText"),
    );

    if (confirmed) {
      handleDeleteCourse(id);
    }
  };

  const deletePDF = async () => {
    const confirmed = await confirm(
      t("delete_pdf"),
      t("delete_pdf_confirmation"),
    );
    if (confirmed) {
      handleDeletePDF();
      setPageNumber(1);
      setScale(1);
      handleCloseMenu();
    }
  };

  const deleteCourseVideo = async () => {
    const confirmed = await confirm(
      t("delete_video"),
      t("delete_video_confirmation"),
    );
    if (confirmed) {
      await handleDeleteCourseVideo(videoUrl);
      setVideo(null);
      handleCloseMenu();
    }
  };
  const tabs = [
    {
      label: t("course"),
      id: "Course",
      TabContent: (
        <Box className={classes.coursesTab} key={`tab-content-course`} sx={{
          py: 1
        }}>
          {(isSectionsLoading ||
            (!isSectionsLoading && sections && sections.length > 0)) && (
            <Card>
              <CardContent>
                <CourseOutline
                  t={t}
                  courseId={courseId}
                  sections={sections}
                  handleClickOpenCreateSection={handleClickOpenCreateSection}
                  handleCreateCourseSection={handleCreateCourseSection}
                  handleUpdateCourseSection={handleUpdateCourseSection}
                  handleDeleteCourseSection={deleteCourseSection}
                  handleUpdateSectionTopic={handleUpdateSectionTopic}
                  handleCreateCourseSectionTopic={
                    handleCreateCourseSectionTopic
                  }
                  handleDeleteCourseSectionTopic={deleteCourseSectionTopic}
                  handleDuplicateTopic={handleDuplicateTopic}
                  handleViewCourseTopic={handleViewCourseTopic}
                  handleUpdateSections={handleUpdateSections}
                  handleUpdateTopics={handleUpdateTopics}
                  handleMoveTopic={handleMoveTopic}
                  isCreator={isCreator}
                  isMember={isMember}
                  completions={memberCompletions}
                  isPublic={isPublic}
                  handleSwitchChange={handleSwitchChange}
                  searchTopics={searchTopics}
                  teachClassrooms={teachClassrooms}
                  classroomId={classroomId}
                  loading={loading}
                  {...otherProps}
                  isLoading={isSectionsLoading}
                  courseSettings={courseSettings}
                  verificationStatus={verificationStatus}
                  handleDeletePDF={handleDeleteSectionPDF}
                  isChallengeModeStudent={isChallengeModeStudent}
                  isReadWriteModeStudent={isReadWriteModeStudent}
                  handleQuickCreateCourseSectionTopic={handleQuickCreateCourseSectionTopic}
                  handleAddTopicsToSection={handleAddTopicsToSection}
                  libraryTopics={libraryTopics}
                  communityTopics={communityTopics}
                  onLoadMoreCommunity={onLoadMoreCommunity}
                  hasMoreCommunity={hasMoreCommunity}
                  isLoadingMoreCommunity={isLoadingMoreCommunity}
                  fileSizeLimit={fileSizeLimit}
                  difficulty={course?.difficulty || "A1"}
                  learnLang={course?.learnLang || attributes?.[`custom:learnLang`] || ""}
                  forLang={course?.forLang || attributes?.[`custom:forLang`] || ""}
                  userImage={userImage}
                  coursePicture={course?.picture}
                  openSnackbar={openSnackbar}
                  coursePictureBuilderColor={course?.pictureBuilderColor}
                  coursePictureBuilderIcon={course?.pictureBuilderIcon}
                  enableFilters
                />
              </CardContent>
            </Card>
          )}
          {!isLoading && !isSectionsLoading && sections.length === 0 && (
            <SectionsNotFound
              t={t}
              handleCreateSection={handleClickOpenCreateSection}
              isCreator={isCreator}
            />
          )}
          {!(isMember || isCreator) && course.visibility === "private" && (
            <Overlay
              description={
                !authenticated
                  ? t("course_sign_in_to_access")
                  : t("join_course_to_access")
              }
              buttonText={!authenticated ? t("sign_in") : t("join")}
              onClick={
                !authenticated
                  ? () => {
                      handleSignIn();
                    }
                  : () => {
                      handleJoin();
                    }
              }
            />
          )}
        </Box>
      ),
    },
    ...(isCreator
      ? [
          {
            label: `${t("members")} (${members.length < 1000 ? members.length : "999+"})`,
            id: "Members",
            disabled: !(isMember || isCreator),
            TabContent: (
              <Box key={`tab-content-members`} sx={{
                py: 1
              }}>
                <MemberList
                  t={t}
                  placeholderImageUrl={memberPlaceholderImageUrl}
                  members={
                    Array.isArray(members) && members.length ? members : []
                  }
                  {...membersProps}
                  disableSubheader
                  getMemberDetails={getMemberDetails}
                  isCreator={isCreator}
                  featureFlags={featureFlags}
                />
              </Box>
            ),
          },
        ]
      : []),
    ...(isCreator
      ? [
          {
            label: t("settings"),
            disabled: isCreator ? false : true,
            id: "Settings",
            TabContent: (
              <Box key={`tab-content-settings`} sx={{
                py: 1
              }}>
                <UpdateCourse
                  t={t}
                  initialValues={course}
                  languages={languages}
                  forLanguages={forLanguages}
                  onSubmit={handleUpdateCourse}
                  fileSizeLimit={fileSizeLimit}
                  subscription={subscription}
                  verificationStatus={verificationStatus}
                  attributes={attributes}
                  openSnackbar={openSnackbar}
                />
              </Box>
            ),
          },
        ]
      : []),
  ];

  let initialTabValue;
  if (window.location.hash) {
    const selectedTab = window.location.hash;
    initialTabValue = tabs.findIndex(
      (tab) => tab.id === selectedTab.replace("#", ""),
    );
    if (initialTabValue === 2) {
      initialTabValue = 0;
      window.location.hash = tabs[0].id;
    }
    if (initialTabValue < 0) {
      initialTabValue = 0;
    }
  } else {
    initialTabValue = 0;
  }
  return (
    <>
      <div className={classes.cardTop}>
        <ProgressiveCardMedia
          sx={(theme) => ({
            [theme.breakpoints.up("md")]: {
              paddingTop: "15%",
            },
            height: 0,
            paddingTop: "25%",
            borderRadius: theme.spacing(2),
            marginBottom: theme.spacing(4),
          })}
          highQualitySrc={(banner ?? picture) || PlaceholderImages.course}
          lowQualitySrc={bannerXs ?? pictureXs}
          title={courseName}
        />
        <TopicDifficultyBadge difficulty={bannerDifficulty} t={t} />
        {userImage ? (
          <CardAvatar
            aria-label={`Open ${courseName} creator's profile`}
            title={createdByName || `${courseName} creator`}
            src={userImage}
            className={classes.cardTopAvatar}
            onClick={() => handleViewUserProfile(createdBy)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleViewUserProfile(createdBy);
              }
            }}
            alt={""}
            role='link'
            tabIndex={0}
            verificationStatus={verificationStatus}
            gameAward={gameAward}
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
              color: theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
            })}
          />
        ) : (
          <CardAvatar
            aria-label={`Open ${courseName} creator's profile`}
            title={createdByName || `${courseName} creator`}
            className={classes.cardTopAvatar}
            onClick={() => handleViewUserProfile(createdBy)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleViewUserProfile(createdBy);
              }
            }}
            alt={""}
            role='link'
            tabIndex={0}
            verificationStatus={verificationStatus}
            gameAward={gameAward}
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.background.default
                  : "white",
            })}
          >
            <Person fontSize='large' />
          </CardAvatar>
        )}
      </div>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography
            className={classes.headerTextTitle}
            variant='h5'
            component='h1'
            data-cy='course-name'
          >
            {courseName}
          </Typography>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            {description}
          </Typography>
          {courseId && isCreator && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}>
              <Typography variant='body2'>
                {t("course_code")}: {courseId}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 2,
              mb: 1,
              [theme.breakpoints.down("md")]: { flexWrap: "wrap", gap: 1 }
            }}>
            {!isMember && !isCreator && (
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<PersonAddIcon />}
                  size='small'
                  onClick={
                    authenticated
                      ? () => {
                          handleJoin();
                        }
                      : () => {
                          handleSignIn();
                        }
                  }
                >
                  {t("join")}
                </Button>
              </Box>
            )}
            {(isCreator || (isMember && !isStudent)) &&
              (!subscription.isSubscriptionLoaded || subscription.isPaidUser) && (
                <Box sx={{
                  mr: 1
                }}>
                  <Button
                    className={classes.inviteButton}
                    variant='contained'
                    color='primary'
                    startIcon={<SchoolIcon />}
                    size='small'
                    onClick={handleOpenAddToClassroom}
                  >
                    {t("add_to_classroom")}
                  </Button>
                </Box>
              )}
            {(isCreator || (isMember && !isStudent)) &&
              subscription.isSubscriptionLoaded &&
              !subscription.isPaidUser && (
                <Box sx={{
                  mr: 1
                }}>
                  <Button
                    className={classes.inviteButton}
                    variant='contained'
                    color='primary'
                    startIcon={<SchoolIcon />}
                    size='small'
                    onClick={handleOpenUpgradeSubscriptionModal}
                  >
                    {t("add_to_classroom")}
                  </Button>
                </Box>
              )}
            {videoUrl && (isMember || isCreator) && (
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<OndemandVideoIcon />}
                  size='small'
                  onClick={handleOpenViewVideo}
                  sx={
                    animatePlayVideo && {
                      animation: `${theme.palette.mode === "light" ? glowPulseLight : glowPulseDark} 2s infinite ease-in-out`,
                      boxShadow: `0 0 0 0 ${theme.palette.mode === "light" ? "rgba(101, 0, 221, 0.7)" : "rgba(171, 120, 255, 0.7)"}`,
                    }
                  }
                >
                  {t("play_video")}
                </Button>
              </Box>
            )}
            {(isMember || isCreator) && !isStudent && (
              <Box sx={{
                mr: 1
              }}>
                <DefaultButton
                  startIcon={<TimelineIcon />}
                  data-cy='view-activity'
                  size='small'
                  onClick={handleOpenSelectClassroomDialog}
                >
                  {t("view_activity")}
                </DefaultButton>
              </Box>
            )}
            {classroomId && isMember && isStudent && (
              <Box sx={{
                mr: 1
              }}>
                <DefaultButton
                  startIcon={<TimelineIcon />}
                  size='small'
                  component={RouterLink}
                  to={`/classrooms/${classroomId}?courseId=${courseId}#Progress`}
                >
                  {t("view_activity")}
                </DefaultButton>
              </Box>
            )}
            {pdfUrl && (isMember || isCreator) && (
              <Box sx={{
                mr: 1
              }}>
                <DefaultButton
                  startIcon={<PictureAsPdfIcon />}
                  size='small'
                  onClick={handleOpenViewPDF}
                >
                  {t("view_pdf")}
                </DefaultButton>
              </Box>
            )}
            {authenticated && (
              <DefaultButton
                startIcon={<MoreVertIcon />}
                size='small'
                onClick={handleClickOpenMenu}
                data-cy='more-options-button'
                aria-expanded={Boolean(anchorEl)}
              >
                {t("more_options")}
              </DefaultButton>
            )}
          </Box>
          <CreatorNotSubscribed
            t={t}
            open={isCreatorNotSubscribedOpen}
            handleClose={handleCloseCreatorNotSubscribedModal}
          />
        </div>
      </div>
      <>
        {tabs.length > 1 && (
          <ResponsiveTabs
            t={t}
            tabs={tabs}
            centered
            initialValue={initialTabValue}
          >
            {tabs.map((tab) => tab.TabContent)}
          </ResponsiveTabs>
        )}
        {tabs.length === 1 && tabs[0].TabContent}
      </>
      <OverflowMenu
        t={t}
        courseId={courseId}
        anchorEl={anchorEl}
        handleClose={handleCloseMenu}
        handleClickOpenMembers={handleClickOpenMembers}
        handleClickOpenCreateSection={handleClickOpenCreateSection}
        handleOpenInviteDialog={handleOpenInviteDialog}
        isCreator={isCreator}
        isShown={isShown}
        isMember={isMember}
        subscription={subscription}
        joinCourse={handleStartCourse}
        leaveCourse={leaveCourse}
        deleteCourse={(id) => deleteCourse(id)}
        handleOpenUpgradeSubscriptionModal={handleOpenUpgradeSubscriptionModal}
        handleClickOpenUpload={handleCLickOpenUpload}
        pdfUrl={pdfUrl}
        videoUrl={videoUrl}
        deletePDF={deletePDF}
        deleteVideo={deleteCourseVideo}
        courseName={courseName}
        trackRecommendedEvent={trackRecommendedEvent}
        mainSiteUrl={mainSiteUrl}
        verificationStatus={verificationStatus}
        gameAward={gameAward}
        loading={loading}
        handleUploadVideo={handleUploadVideo}
        isTeacher={!isStudent}
        video={video}
        setVideo={setVideo}
        handleCloseMenu={handleCloseMenu}
      />
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
      <MembersDialog
        t={t}
        placeholderImageUrl={memberPlaceholderImageUrl}
        dialogTitle={courseName}
        open={isMembersOpen}
        handleClose={handleCloseMembers}
        members={members}
        {...membersProps}
        getMemberDetails={getMemberDetails}
      />
      <CreateSectionDialog
        t={t}
        section={section}
        handleInputChange={handleSectionChange}
        open={isCreateSectionOpen}
        handleClose={handleCloseCreateSection}
        handleSubmit={handleAddSection}
      />
      <InviteDialog
        t={t}
        open={isInviteDialogOpen}
        handleClose={handleCloseInviteDialog}
        inviteLink={`${window.location.origin}/courses/${courseId}?plan=learn&learnlang=${course.learnLang}`}
        enrolmentKey={
          course.visibility === "private" ? course.enrolmentKey : ""
        }
        inviteType={"course"}
        attributes={attributes}
        trackRecommendedEvent={trackRecommendedEvent}
        itemId={courseId}
        teachClassrooms={
          teachClassrooms.filter(
            (classroom) =>
              classroom.learnLang === course.learnLang ||
              classroom?.courses?.includes(courseId),
          ) || []
        }
        addCourseToClassroom={addCourseToClassroom}
        learnLang={course.learnLang}
      />
      <ResponsiveDialog
        open={isUploadPdfOpen}
        dialogTitle={t("upload_pdf")}
        handleClose={() => setIsUploadPdfOpen(false)}
        handleSubmit={handleUploadSubmit}
        isFullScreen={false}
        isSubmitDisabled={!file || isSubmitting}
        maxWidth={"md"}
      >
        <PdfUploader t={t} file={file} setFile={setFile} multiple={false} />
      </ResponsiveDialog>
      <PDFViewer
        t={t}
        pdfUrl={pdfUrl}
        pdfTitle={pdfTitle}
        open={isViewPdfOpen}
        handleClose={handleCloseViewPdf}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        scale={scale}
        setScale={setScale}
      />
      <VideoViewer
        t={t}
        title={courseName}
        s3Url={videoUrl}
        open={isViewVideoOpen}
        handleClose={() => setIsViewVideoOpen(false)}
      />
    </>
  );
}

export default function ViewCourse({
  t = (text) => text,
  placeholderImageUrl,
  isLoading,
  course = {},
  handleSearch,
  isStudent,
  addCourseToClassroom,
  openSnackbar,
  handleDeleteCourseVideo,
  isChallengeModeStudent,
  isReadWriteModeStudent,
  handleQuickCreateCourseSectionTopic,
  handleAddTopicsToSection,
  ...otherProps
}) {
  const { classes } = useStyles();
  const safeCourse = course || {};
  return (
    <div className={classes.root}>
      {isLoading && Object.keys(safeCourse).length === 0 && (
        <>
          <Course
            t={t}
            isLoading={isLoading}
            isStudent={isStudent}
            {...otherProps}
          />
        </>
      )}
      {!isLoading && course && Object.keys(safeCourse).length && (
        <>
          <Course
            t={t}
            isLoading={isLoading}
            course={course}
            handleDeleteCourseVideo={handleDeleteCourseVideo}
            addCourseToClassroom={addCourseToClassroom}
            openSnackbar={openSnackbar}
            isStudent={isStudent}
            isChallengeModeStudent={isChallengeModeStudent}
            isReadWriteModeStudent={isReadWriteModeStudent}
            handleQuickCreateCourseSectionTopic={handleQuickCreateCourseSectionTopic}
            handleAddTopicsToSection={handleAddTopicsToSection}
            {...otherProps}
          />
        </>
      )}
      {!isLoading && Object.keys(safeCourse).length === 0 && (
        <CourseNotFound
          t={t}
          placeholderImageUrl={placeholderImageUrl}
          handleSearch={handleSearch}
        />
      )}
    </div>
  );
}
